import { api } from "@/services/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import useEvents from "@/modules/calendar/stores/use-events"
import { toast } from "sonner"
import { isClientError } from "@educabot/fetcher"
import { useState } from "react"
import { isAfter, isSameDay, parseISO, startOfDay } from "date-fns"

interface DeleteHookParams {
  eventId: string
  isBulkDelete: boolean
  originalEvent: UserEvent
}

interface ApiDeleteVariables {
  eventId: string
  isBulkDelete: boolean
}

export default function useDeleteEvent() {
  const queryClient = useQueryClient()
  const { deleteEvents: deleteEventInStore, addEvents: addEventsToStore, events } = useEvents()

  const [isUndoableActionInProgress, setIsUndoableActionInProgress] = useState(false)
  const [currentTimeoutId, setCurrentTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [eventPendingApiDelete, setEventPendingApiDelete] = useState<UserEvent | null>(null)

  const actualDeleteMutation = useMutation<unknown, Error, ApiDeleteVariables>({
    mutationFn: (params: ApiDeleteVariables) => api.cronos.user.events.delete(params),
    onSuccess: (_data, variables) => {
      const message = variables.isBulkDelete
        ? "Los eventos han sido eliminados permanentemente."
        : "El evento ha sido eliminado permanentemente."
      toast.success(message)
      queryClient.invalidateQueries({ queryKey: ["get-daily-calendar"] })
      setIsUndoableActionInProgress(false)
      setEventPendingApiDelete(null)
    },
    onError: (error, _variables, _context) => {
      toast.error("Error al eliminar permanentemente", {
        description: isClientError(error) ? error.message : "No se pudo completar la eliminación.",
      })
      if (eventPendingApiDelete) {
        addEventsToStore([eventPendingApiDelete])
        setEventPendingApiDelete(null)
      }
      setIsUndoableActionInProgress(false)
    },
  })

  const initiateDeleteWithUndo = (params: DeleteHookParams) => {
    const { eventId, isBulkDelete, originalEvent } = params

    if (currentTimeoutId) {
      clearTimeout(currentTimeoutId)
      setEventPendingApiDelete(null)
    }
    if (actualDeleteMutation.isPending) {
      toast.error("Otra operación de eliminación ya está en curso.")
      return
    }

    deleteEventInStore(originalEvent, isBulkDelete)
    setIsUndoableActionInProgress(true)
    setEventPendingApiDelete(originalEvent)

    const deleteTimeoutDuration = 5000

    const newTimeoutId = setTimeout(() => {
      actualDeleteMutation.mutate({ eventId, isBulkDelete })
    }, deleteTimeoutDuration)
    setCurrentTimeoutId(newTimeoutId)

    const bulkedList = events.filter((event) => {
      const eventStartDate = startOfDay(parseISO(event.startDate))
      const eventRefStartDate = startOfDay(parseISO(originalEvent.startDate))

      const isSameOrAfter =
        isSameDay(eventStartDate, eventRefStartDate) || isAfter(eventStartDate, eventRefStartDate)
      const isSiblingEvent = event.parent.id === originalEvent.parent.id

      return !isSiblingEvent || (isSiblingEvent && !isSameOrAfter)
    })
    const toastId = `delete-toast-${eventId}-${Date.now()}`

    toast("Evento marcado para eliminación.", {
      id: toastId,
      description: `Se eliminará en ${deleteTimeoutDuration / 1000} segundos.`,
      action: {
        label: "Deshacer",
        onClick: () => {
          if (newTimeoutId) clearTimeout(newTimeoutId)
          setCurrentTimeoutId(null)
          addEventsToStore([originalEvent, ...bulkedList])
          toast.info("Eliminación deshecha.")
          setIsUndoableActionInProgress(false)
          setEventPendingApiDelete(null)
          toast.dismiss(toastId)
          if (isBulkDelete) {
            window.location.reload()
          }
        },
      },
      duration: deleteTimeoutDuration + 1000,
    })
  }

  return {
    isDeleting: actualDeleteMutation.isPending || isUndoableActionInProgress,
    initiateDeleteWithUndo,
  }
}
